import { getWorkspace } from '../session/sessionStorage'
import { useTranslation } from 'react-i18next'
import { useFieldsInterpolationsFetcher } from './fields'
import { useRawFieldsInterpolationsFetcher } from './rawFields'
import { useRawRepliesInterpolationsFetcher } from './rawReplies'
import { useRepliesInterpolationsFetcher } from './replies'

export const useServiceOrderInterpolationsFetcher = (skip, checklistId, placeType, opts) => {
  const { t } = useTranslation('serviceOrders')
  const repliesOptions = useRepliesInterpolationsFetcher(skip, 'self.replies', checklistId, opts)
  const fieldsOptions = useFieldsInterpolationsFetcher(skip, 'self.field_values', 'service_orders')
  const placeFieldsOptions = useFieldsInterpolationsFetcher(skip || !placeType, 'self.place.field_values', placeType, {
    group: t('interpolations.groups.destination')
  })
  const workspace = getWorkspace()

  if (skip) return []

  return [
    {
      name: t('interpolations.activityCode'),
      id: '{{service_order.yearly_id}}',
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.activityName'),
      id: '{{service_order.name}}',
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.activityDescription'),
      id: '{{service_order.description}}',
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.checklistName'),
      id: '{{service_order.checklist.name}}',
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.status'),
      id: '{{service_order.status.name}}',
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.projectName'),
      id: '{{service_order.project.name}}',
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.placeName'),
      id: '{{place.name}}',
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.publicResumeLink'),
      id: `https://${workspace}.btime.io/resume/{{service_order.id}}?t={{service_order.resume_token}}`,
      type: 'string',
      group: t('interpolations.groups.activity')
    },
    {
      name: t('interpolations.publicExecutionLink'),
      id: `https://${workspace}.btime.io/service-orders/{{service_order.id}}/execution?t={{service_order.execution_token}}`,
      type: 'string',
      group: t('interpolations.groups.activity')
    },

    {
      name: t('interpolations.userName'),
      id: '{{anonymous service_order.user.name}}',
      type: 'string',
      group: t('interpolations.groups.user')
    },
    {
      name: t('interpolations.userEmail'),
      id: '{{service_order.user.email}}',
      type: 'string',
      group: t('interpolations.groups.user')
    },
    {
      name: t('interpolations.userGroup'),
      id: '{{service_order.user.contract.group.name}}',
      type: 'string',
      group: t('interpolations.groups.user')
    },

    {
      name: t('interpolations.destinationName'),
      id: '{{service_order.place.name}}',
      type: 'string',
      group: t('interpolations.groups.destination')
    },
    {
      name: t('interpolations.fullDestinationName'),
      id: '{{service_order.place.resume}} - {{service_order.place.name}}',
      type: 'string',
      group: t('interpolations.groups.destination')
    },

    ...placeFieldsOptions,

    {
      name: t('interpolations.schedulingDate'),
      id: '{{service_order.scheduling}}',
      type: 'datetime',
      group: t('interpolations.groups.dates')
    },
    {
      name: t('interpolations.openingDate'),
      id: '{{service_order.start_date}}',
      type: 'datetime',
      group: t('interpolations.groups.dates')
    },
    {
      name: t('interpolations.completionDate'),
      id: '{{service_order.end_date}}',
      type: 'datetime',
      group: t('interpolations.groups.dates')
    },

    ...fieldsOptions,
    ...repliesOptions
  ]
}

export const useServiceOrderRawInterpolationsFetcher = (skip, checklistId) => {
  const { t } = useTranslation('serviceOrders')
  const rawRepliesOptions = useRawRepliesInterpolationsFetcher(skip, 'self.replies', checklistId)
  const rawFieldsOptions = useRawFieldsInterpolationsFetcher(skip, 'self.service_order.field_values', 'service_orders')

  if (skip) return []

  return [
    { name: t('interpolations.savedActivityUser'), id: '::{{service_order.user_id}}', type: 'raw_user' },

    { name: t('interpolations.updatedActivityPlace'), id: '::{{service_order.place_id}}', type: 'raw_place' },

    { name: t('interpolations.updatedActivityAsset'), id: '::{{service_order.asset_id}}', type: 'raw_asset' },

    {
      name: t('interpolations.savedActivityChecklist'),
      id: '::{{service_order.checklist_id}}',
      type: 'raw_checklist'
    },

    {
      name: t('interpolations.schedulingDate'),
      id: '::{{service_order.scheduling}}',
      type: 'raw_datetime',
      group: t('interpolations.groups.dates')
    },
    {
      name: t('interpolations.openingDate'),
      id: '::{{service_order.start_date}}',
      type: 'raw_datetime',
      group: t('interpolations.groups.dates')
    },
    {
      name: t('interpolations.completionDate'),
      id: '::{{service_order.end_date}}',
      type: 'raw_datetime',
      group: t('interpolations.groups.dates')
    },

    ...rawFieldsOptions,
    ...rawRepliesOptions
  ]
}
